import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    usePerformBoOperationMutation,
    type PerformBoOperationVariables,
} from "@src/businessModules/WorkShop/shared/bo/usePerformBoOperationMutation";
import {
    actOfCheckingAvailabilityServices,
    type ActOfCheckingAvailabilityContent,
    type ActOfCheckingAvailabilityContentInsertData,
    type ActOfCheckingAvailabilityContentUpdateFactQtyData,
} from "../../../../api/actOfCheckingAvailability";
import { PARTIAL_INVENTORY_CONTENT_INCLUDES } from "./hooks";

/** Параметры хука мутаций карточки частичной инвентаризации склада. */
export interface UseStockPartialInventoryCardMutationsParams {
    /**
     * Колбэк синхронизации обновлённой строки АПН после сохранения фактического количества.
     * @param contentCode код строки состава АПН
     */
    onContentRefetch?: (contentCode: number) => void | Promise<unknown>;
    /**
     * Колбэк после insert: положить enriched-строку в кэш списка.
     * @param content строка состава АПН
     */
    onContentUpsert?: (content: ActOfCheckingAvailabilityContent) => void;
    /** Колбэк успешной BO-операции. */
    onBoOperationSuccess?: () => void;
}

/** Мутации карточки частичной инвентаризации склада. */
export interface UseStockPartialInventoryCardMutationsResult {
    /** Добавление строки состава АПН (scan → insert). */
    insertContentMutation: UseMutationResult<
        ActOfCheckingAvailabilityContent,
        Error,
        ActOfCheckingAvailabilityContentInsertData
    >;
    /** Обновление фактического количества строки АПН. */
    updateFactQtiesMutation: UseMutationResult<void, Error, ActOfCheckingAvailabilityContentUpdateFactQtyData>;
    /** Выполнение BO-операции над АПН. */
    performBoOperationMutation: UseMutationResult<void, Error, PerformBoOperationVariables>;
}

/** Screen-level мутации карточки частичной инвентаризации склада. */
export function useStockPartialInventoryCardMutations({
    onContentRefetch,
    onContentUpsert,
    onBoOperationSuccess,
}: UseStockPartialInventoryCardMutationsParams): UseStockPartialInventoryCardMutationsResult {
    const insertContentMutation = useMutation({
        mutationKey: ["stock", "partialInventory", "insertContent"],
        meta: { errorSource: "stock.partialInventory.insertContent" },
        mutationFn: async (variables: ActOfCheckingAvailabilityContentInsertData) => {
            const inserted = await actOfCheckingAvailabilityServices.content.insert({ data: variables });
            const enriched = await actOfCheckingAvailabilityServices.content.loadByCode({
                code: inserted.Code,
                actCode: variables.actCode,
                ...PARTIAL_INVENTORY_CONTENT_INCLUDES,
            });

            return enriched ?? inserted;
        },
        onSuccess: (content) => {
            onContentUpsert?.(content);
        },
    });

    const updateFactQtiesMutation = useMutation({
        mutationKey: ["stock", "partialInventory", "updateFactQties"],
        meta: { errorSource: "stock.partialInventory.updateFactQties" },
        mutationFn: (variables: ActOfCheckingAvailabilityContentUpdateFactQtyData) =>
            actOfCheckingAvailabilityServices.content.updateFactQties({ data: variables }),
        onSuccess: async (_data, variables) => {
            notification.success("Фактическое количество сохранено");
            await onContentRefetch?.(variables.contentCode);
        },
    });

    const performBoOperationMutation = usePerformBoOperationMutation({
        scope: "stockPartialInventory",
        onSuccess: onBoOperationSuccess,
    });

    return {
        insertContentMutation,
        updateFactQtiesMutation,
        performBoOperationMutation,
    };
}
